import React, { useEffect, useRef } from "react";
import { InView } from "react-intersection-observer";
import { Checkbox } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { SearchTypeForTrack } from "./utils";

import CommonItem from "./CommonListItem";
import listChangeHoc from "./listChangeHOC";
import { useListKeyboardNavigation } from "./hooks/useListKeyboardNavigation";
import { useLatest } from "./hooks/useLatest";

import {
    IItemData,
    ChangeParams,
    IsCheckAllParams,
    CheckAllParams,
} from "./common.typing";

import locale from "../../_util/locale";

export interface CommonListProps {
    /**
     * 列表类型
     * - member
     * - group
     * - session
     * - partner
     * - contact 外部联系人
     * - combination (组合类型，包括 member、session)
     * - default: member
     */
    type?: string;
    /**
     * 组件类型（四种组件策略）
     * member 选人组件
     * group 选群组件
     * session 选会话组件
     * org 选部门
     */
    pikerType?: string;
    isShowOrg: boolean;
    title?: string;
    showCheckAll?: boolean;
    customStyle?: {};
    list?: IItemData[];
    disabledids?: string[];
    currentList?: IItemData[];
    setCurrentList: (list: IItemData[]) => void;
    onChangeHoc: (params: ChangeParams) => void;
    onCheckAllHoc: (params: CheckAllParams) => void;
    isCheckAllHoc: (params: IsCheckAllParams) => boolean;
    requiredList?: IItemData[];
    onClickPartner?: (id: number | string, name: string) => void;
    onChangeCurPage: (page: string) => void;
    isShowGroupMemberRole?: boolean;
    multiple?: boolean;
    isSearchHighlight?: boolean;
    trackTitle?: string; // 埋点所需的选人组件标题
    isNeedTrack?: boolean; // 埋点所需：是否需要埋点
    isFromSearch?: boolean; // 埋点所需：是否来自搜索
    searchKey?: string; // 埋点所需：搜索关键词（搜索时才有）
    searchType?: string; // 埋点所需：搜索类型（搜索时才有）
    track: (eventName: string, eventData?: any) => void;
}

const CommonList: React.FC<CommonListProps> = ({
    type = "member",
    pikerType,
    isShowOrg,
    title,
    showCheckAll = false,
    customStyle,
    list = [],
    currentList = [],
    setCurrentList,
    disabledids = [],
    onChangeHoc,
    onCheckAllHoc,
    isCheckAllHoc,
    requiredList,
    onChangeCurPage,
    onClickPartner,
    isShowGroupMemberRole = false,
    multiple = true,
    isSearchHighlight = false,
    isNeedTrack = false,
    searchKey,
    searchType,
    trackTitle,
    track,
    isFromSearch = false,
}) => {
    // ==================== Refs ====================
    const commonListRef = useRef<HTMLDivElement>(null);
    // 使用 useLatest 替代 useEffect 同步 ref 的反模式
    const listRef = useLatest(list);
    const currentListRef = useLatest(currentList);
    const clickedIndexs = useRef<number[]>([]);
    const expoedIndexs = useRef<number[]>([]);

    /**
     * 判断某项是否禁用
     * @param id 项目ID
     * @param itemType 项目类型，当为 "partner" 时使用特殊处理逻辑
     */
    const isDisabled = (id: string | number, itemType?: string) => {
        const requiredIds = requiredList ? requiredList.map(v => v.id) : [];
        const disabled =
            itemType === "partner"
                ? disabledids.includes(
                      `${id.toString().split("-partner-")[0]}-partner-`
                  )
                : disabledids.includes(id.toString());
        const requiredDisabled = requiredIds.includes(id);
        return disabled || requiredDisabled;
    };

    const { highlightedIndex, setHighlightedIndex, handlePointerMove } = useListKeyboardNavigation({
        containerRef: commonListRef,
        listRef,
        dataAttribute: "data-index",
        onSelectItem: (index, item) => {
            const listData = listRef.current;
            const selectedList = currentListRef.current;
            const isSelected = selectedList.some(selectedItem => selectedItem && selectedItem.id === item.id);
            onChangeHoc({
                value: item.id,
                checked: !isSelected,
                list: listData,
                currentList: selectedList,
                setCurrentList,
            });
        },
        isItemDisabled: (item) =>
            item && typeof item.id !== "undefined" && isDisabled(item.id.toString(), type),
    });

    // 当 list 变化时重置高亮索引
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [list, setHighlightedIndex]);

    // 处理曝光埋点
    const handleInViewChange = (
        inView: boolean,
        entry: IntersectionObserverEntry,
        index: number
    ) => {
        if (isFromSearch) {
            if (inView && !expoedIndexs.current.includes(index)) {
                expoedIndexs.current.push(index);
                const trackData = {
                    pageName: "PSelect_002",
                    chat_name: trackTitle,
                    search_word: searchKey,
                    OrderNumber: index + 1,
                    search_type: searchType
                        ? SearchTypeForTrack[searchType]
                        : undefined,
                    $element_name: "278",
                };
                track("Expo_Chat_Element", trackData);
            }
        }
    };

    const onChange = (e: CheckboxChangeEvent, index: number) => {
        const { value, checked } = e.target;
        if (isFromSearch) {
            // 搜索时，只上报一次
            if (!clickedIndexs.current.includes(index)) {
                clickedIndexs.current.push(index);
                track("Click_Chat_Element", {
                    pageName: "PSelect_002",
                    chat_name: trackTitle,
                    search_word: searchKey,
                    OrderNumber: index + 1,
                    search_type: searchType
                        ? SearchTypeForTrack[searchType]
                        : undefined,
                    $element_name: "278",
                });
            }
        } else if (isNeedTrack && checked) {
            // 非搜索时
            track("Click_Chat_Element", {
                pageName: "PSelect_001",
                chat_name: trackTitle,
                OrderNumber: index + 1,
                $element_name: "PSelect_003",
            });
        }
        onChangeHoc({
            value,
            checked,
            list,
            currentList,
            setCurrentList
        });

        // 同步高亮索引到点击的项
        setHighlightedIndex(index);
    };

    const onCheckAll = (e: CheckboxChangeEvent) => {
        const { value, checked } = e.target;
        onCheckAllHoc({
            value,
            checked,
            list,
            disabledids,
            currentList,
            requiredList,
            setCurrentList,
        });
    };

    const handleClickPartner = (id, name) => {
        if (isPartnerDisabled(id)) return;
        onClickPartner && onClickPartner(id, name);
    };

    const isPartnerDisabled = id => {
        const curDisabled = !!currentList.filter(item => item.id === id).length;
        return isDisabled(id) || curDisabled;
    };

    // 是否展示群查看按钮
    const getIsShowGoGroupChildBtn = (item: IItemData) => {
        return (
            (type === "combination" || // 一级条件:列表是组合类型
                ((pikerType === "member" || pikerType === "session") &&
                    type === "group")) && // 一级条件:列表是群列表 且 组件类型是选人或会话，也需要可以下探
            (item.type === "team" || item.id?.toString().includes("-team-")) // 二级条件:保证是群
        );
    };

    return (
        <div className="commonListBox" tabIndex={0}>
            {!!title && (
                <p className="commonListTitle">
                    <span>{title}</span>
                </p>
            )}
            <div className="commonList" ref={commonListRef}>
                {showCheckAll && multiple && list.length > 0 && (
                    <div className="commonListCheckAll">
                        <Checkbox
                            checked={isCheckAllHoc({
                                list,
                                currentList,
                                disabledids,
                            })}
                            onChange={onCheckAll}
                        >
                            <span className="name">
                                {locale("useradd_all_select")}
                            </span>
                        </Checkbox>
                    </div>
                )}
                <Checkbox.Group
                    style={{ width: "100%" }}
                    value={currentList.map(v => v.id)}
                >
                    {list.map((item, index) => (
                        <InView
                            key={item.id || item.key || index}
                            triggerOnce={true}
                            threshold={0}
                            onChange={(inView, entry) => {
                                handleInViewChange(inView, entry, index);
                            }}
                        >
                            {({ ref }) => (
                                <div
                                    className={`commonListCheck ${highlightedIndex === index ? "highlighted" : ""}`}
                                    style={customStyle}
                                    ref={ref}
                                    data-index={index}
                                    onClick={e => {
                                        e.stopPropagation();
                                    }}
                                    onPointerMove={() => handlePointerMove(index)}
                                >
                                    <Checkbox
                                        value={item.id}
                                        onChange={e => onChange(e, index)}
                                        disabled={isDisabled(
                                            item.id.toString(),
                                            type
                                        )}
                                    >
                                        <CommonItem
                                            {...{
                                                type,
                                                pikerType,
                                                isShowOrg,
                                                item,
                                                isShowGroupMemberRole,
                                                isSearchHighlight,
                                            }}
                                        />
                                    </Checkbox>
                                    {/* 我的伙伴查看按钮 */}
                                    {type === "partner" && (
                                        <span
                                            className={`partnerView ${
                                                isPartnerDisabled(
                                                    item.id.toString()
                                                )
                                                    ? "disabled"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                handleClickPartner(
                                                    item.id,
                                                    item.name
                                                )
                                            }
                                        >
                                            {locale("partner_view")}
                                        </span>
                                    )}
                                    {/* 群查看按钮 */}
                                    {getIsShowGoGroupChildBtn(item) && (
                                        <span
                                            className={`partnerView ${
                                                isPartnerDisabled(
                                                    item.id.toString()
                                                )
                                                    ? "disabled"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                handleClickPartner(
                                                    item.id,
                                                    item.name
                                                )
                                            }
                                        >
                                            {locale("partner_view")}
                                        </span>
                                    )}
                                </div>
                            )}
                        </InView>
                    ))}
                </Checkbox.Group>
            </div>
        </div>
    );
};

export default listChangeHoc(CommonList);
